(ns hai.notes.page
  "The notes wall: sticky page chrome — a header bar with the title, search,
   applied-filter chips and actions; the facet sidebar; a pinned pagination
   footer — around the shared collection, rendering every note as the canonical
   read-only card ([[hai.notes.card/card]])."
  (:require
   [clojure.string :as str]
   [hai.sharing.components :as sharing.components]
   [hai.sharing.helper :as sharing]
   [hai.notes.card :as note-card]
   [hai.notes.create :as note-create]
   [hai.notes.data :as data]
   [hai.notes.facets :as facets]
   [hai.ui.components :as c]
   [hai.ui.components.card :as card]
   [hai.ui.components.collection.bulk-actions :as bulk-actions]
   [hai.ui.components.collection.table :as collection]
   [hai.ui.components.collection.views.cards :as cards]
   [hai.ui.components.collection.views.table :as table]
   [hai.ui.components.form.core :as form]
   [hai.ui.icons :as icons]
   [hai.ui.rf-sugar :refer [>e <s]]
   [hai.ui.tokens :as t]
   [hai.web-app.layout :as layout]
   [lambdaisland.ornament :as o]
   [reagent.core :as r]
   [url.core :as url]))

(def ^:private trash-icon (icons/mui-icon :outlined :delete))

(defn- format-date
  "Locale date label. The value arrives as an ISO string or as anything with
  `.getTime` (js/Date, goog.date.DateTime from the resolve layer) — locale
  formatting lives on js/Date, so non-strings are round-tripped through one."
  [iso-or-date]
  (try
    (let [d (if (string? iso-or-date)
              (js/Date. iso-or-date)
              (js/Date. (.getTime iso-or-date)))]
      (.toLocaleDateString d))
    (catch :default _ nil)))

(defn- note-card-render
  [_collection {note :form/current-value}]
  (let [note-uuid     (:note/uuid note)
        activity-urls (->> (:note/activities note)
                           (keep :activity/url)
                           vec)]
    [note-card/card
     {:note            note
      :tags            (vec (:note/tags note))
      :link-urls       activity-urls
      :contacts        (vec (:note/contacts note))
      :linked-notes    (vec (:note/notes note))
      :linked-tasks    (vec (:note/tasks note))
      :share?          (sharing/can-share? note)
      :on-share-closed (fn []
                         (>e [:table/resolve data/table-id])
                         (>e [::data/load-facets]))
      :on-body-click   (fn [] (>e [:routing/navigate [:notes/detail note]]))
      :on-delete       (when (sharing/owner? note)
                         (fn [] (>e [::data/delete note-uuid])))}]))

(defn- table-actions-render
  [{:note/keys [uuid] :as note}]
  (r/with-let [!delete-open? (r/atom false)]
    (let [can-del? (sharing/owner? note)
          actions  (cond-> []
                     can-del? (conj ["Delete" (fn [_] (reset! !delete-open? true)) trash-icon]))]
      (if (seq actions)
        [:<>
         [c/AlertDialogRoot
          {:open @!delete-open? :onOpenChange #(reset! !delete-open? %)}
          [c/AlertDialogContent
           [c/AlertDialogTitle "Delete note"]
           [c/AlertDialogDescription "Are you sure you want to delete this note?"]
           [c/Flex {:justify "end" :gap "3" :mt "4"}
            [c/Button {:variant "soft" :color "gray" :on-click #(reset! !delete-open? false)}
             "Cancel"]
            [c/Button {:variant "solid" :color "red"
                       :on-click (fn []
                                   (reset! !delete-open? false)
                                   (>e [::data/delete uuid]))}
             "Delete"]]]]
         [table/more-menu-cell actions]]
        [c/TableCell]))))

;; Filter values are set from the facet sidebar ([[hai.notes.facets/panel]]) and
;; cleared from the header chips; no per-column filter widget ever renders. The
;; collection's Filter schema still requires a `:field`, hence this stub.
(defn- sidebar-only-filter-field
  [_field-props]
  nil)

;; Pill faces: `(value column) -> hiccup` renderers for the applied-filter chips
;; in the page header, keyed off the live filter value.

(defn- tag-summary
  "Chip face: the chosen tag, `#`-prefixed so it reads as a tag (`#work`), or
   the `:untagged` sentinel's label."
  [current-value _column]
  (cond
    (keyword? current-value) "Untagged"
    (first current-value)    (str "#" (first current-value))
    :else                    "Any tag"))

(defn- contact-summary
  "Chip face: the chosen contact's name, looked up by uuid in the loaded facet
   options; the `:no-contact` sentinel's label; or a neutral empty label."
  [current-value _column]
  (cond
    (keyword? current-value) "No contact"
    :else (or (some (fn [{contact-uuid :contact/uuid full-name :contact/full-name}]
                      (when (= contact-uuid current-value) full-name))
                    (:facet/contacts @(<s [::data/facets])))
              "Anyone")))

(defn- domain-summary
  "Chip face for the URL filter: the domain itself, or — when an exact page is
   picked — its schemeless `host/path` form (`url->display-label`) so the domain
   stays visible (`github.com/owner/repo`, not a bare `/owner/repo`)."
  [current-value _column]
  (let [[kind target] current-value]
    (case kind
      :none    "No web page"
      :domain  target
      :url     (url/url->display-label target)
      "Any URL")))

;; ---------------------------------------------------------------------------
;; Sticky page header
;; ---------------------------------------------------------------------------

(o/defstyled page-header :header
  {:position "sticky" :top "0" :z-index "50"
   :display "flex" :align-items "center" :flex-wrap "wrap"
   :gap "10px"
   :padding "10px 14px"
   :background t/--notes
   :border-bottom-width "2px" :border-bottom-style "dotted"
   :border-bottom-color t/--note-sage-35
   :border-radius "10px"}
  [:.header-icon
   {:display "inline-flex" :align-items "center"
    :color t/--note-kraft}
   ;; Stroke icon — undo the page's `svg {fill: currentColor}`.
   [:svg {:fill "none"}]]
  [:.header-title
   {:font-size "16px" :font-weight "700" :line-height "1.2"
    :margin "0" :color t/--note-kraft}]
  [:input.header-search
   {:flex "0 1 340px" :min-width "160px"
    :padding "7px 12px"
    :border-width "1px" :border-style "solid" :border-color t/--note-sage-35
    :border-radius "8px"
    :background t/--colour-snow
    :color t/--note-kraft
    :font-size "13px" :font-family "inherit"
    :outline "none"
    :transition "border-color 0.15s ease"}
   [:&::placeholder {:color t/--note-sage-55}]
   [:&:focus {:border-color t/--note-sage-55}]]
  [#{:.chips-slot :.table-extras} {:display "contents"}]
  [:.header-actions
   {:display "flex" :align-items "center" :gap "4px" :margin-left "auto"}]
  [:.header-btn
   {:width "28px" :height "28px"
    :border "none" :border-radius "999px"
    :background "transparent" :padding "0"
    :color t/--note-kraft :cursor "pointer"
    :display "inline-flex" :align-items "center" :justify-content "center"
    :transition "background 0.15s ease"}
   [#{:&:hover :&.active} {:background t/--note-kraft-10}]
   [:svg {:width "16px" :height "16px" :fill "currentColor"}]
   ;; Labeled variant (the filter + new-note buttons): icon + text, sized to
   ;; content. The bare ⋮ settings button keeps the round icon-only base.
   [:&.labeled
    {:width "auto" :gap "6px" :padding "0 12px"
     :font-family "inherit" :font-size "13px" :font-weight "600"}]
   ;; Filled "Add note" button — the deeper notes butter yellow. Its own hover
   ;; keeps the fill (the shared header-btn hover would blank it back to
   ;; transparent).
   [:&.add
    {:background t/--note-add-fill}
    [#{:&:hover :&.active} {:background t/--note-add-fill :filter "brightness(0.95)"}]]])

;; The kebab popover's rows — the collection settings form's own look
;; (`settings/form-render`), minus its "Show filters" row: the header's funnel
;; toggle owns that now.
(o/defstyled settings-rows :div
  :flex :flex-col :gap-2
  [:& :> [:div :flex :flex-row :items-center :gap-2]])

(defn- header-controls
  "`:form/render` for the collection's settings sub-form, mounted in the page
   header's actions cluster: the show/hide-filters toggle (writes both
   per-view \"Show filters\" settings, so it persists and survives view
   switches), the new-note button, and the ⋮ settings popover — Admin view,
   Case sensitive, and the View mode select, all the collection's own fields,
   so their persistence + reload wiring is untouched."
  [{fields :form/fields}]
  (let [{:form.field/keys [current-value]} (get fields :collection/view)
        cards?      (not= :collection.view/table current-value)
        card-flag   (get fields :show-filters-card?)
        table-flag  (get fields :show-filters-table?)
        filters-on? (boolean (:form.field/current-value (if cards? card-flag table-flag)))
        ;; The sidebar is view-independent chrome, so the toggle writes BOTH
        ;; per-view "Show filters" settings — switching views can't then
        ;; surprise-show or -hide it.
        toggle!     (fn []
                      ((:form.field/set-value! card-flag) (not filters-on?))
                      ((:form.field/set-value! table-flag) (not filters-on?)))]
    [:<>
     [c/Tooltip {:content (if filters-on? "Hide filters" "Show filters")}
      [:button.header-btn
       {:type       "button"
        :class      ["labeled" (when filters-on? "active")]
        :aria-label (if filters-on? "Hide filters" "Show filters")
        :on-click   toggle!}
       [icons/filled-filter_alt]
       [:span "Filter"]]]
     [c/Tooltip {:content "Make a note"}
      [:button.header-btn
       {:type       "button"
        :class      ["labeled" "add"]
        :aria-label "Make a note"
        :on-click   #(>e [::note-create/open])}
       [icons/plus {:size 16}]
       [:span "Add Note"]]]
     [c/PopoverRoot
      [c/PopoverTrigger
       [:button.header-btn {:type "button" :aria-label "Notes view settings"}
        [icons/filled-more_vert]]]
      [c/PopoverContent {:align "end"}
       [settings-rows
        (when @(<s :auth/admin?)
          [:label
           [:div
            [form/checkbox (get fields :admin?)]
            [c/Text "Admin view"]]])
        [:label
         [:div
          [form/checkbox (get fields :case-sensitive?)]
          [c/Text {:title "sorting, search, filtering"} "Case sensitive"]]]
        [form/render-field (get fields :collection/view)]]]]]))

(defn- page-chrome
  "`:controls` for the notes collection — the sticky page header: title, the
   always-visible search input (the collection's `:table/search` field), the
   applied-filter chips, and the actions cluster. In table view the
   bulk-actions dropdown and spreadsheet export stay available ahead of it."
  [table]
  (let [{:form.field/keys [current-value set-value!]}
        (get-in table [:table/form :form/fields :table/search])
        view           (-> table :table/form :form/current-value
                           :table/settings :collection/view)
        cards?         (not= :collection.view/table view)
        settings-field (get-in table [:table/form :form/fields :table/settings])]
    [page-header
     [:span.header-icon [icons/note {:size 18}]]
     [:h1.header-title "Notes"]
     [:input.header-search
      {:type        "text"
       :value       (or current-value "")
       :placeholder "Search notes…"
       :aria-label  "Search notes"
       ;; The facet counts follow the search, on the same debounce cadence as
       ;; the table's own resolve.
       :on-change   (fn [e]
                      (set-value! (.. e -target -value))
                      (>e [::data/load-facets-debounced]))}]
     ;; Always-present display:contents slots keep every header child at a fixed
     ;; position: a conditional sibling appearing would otherwise shift the
     ;; settings form/component and remount it, resetting the settings sub-form
     ;; (kebab ✓ / funnel state) to defaults.
     [:span.chips-slot [facets/applied-chips table]]
     [:div.header-actions
      [:span.table-extras
       (when-not cards?
         [:<>
          [bulk-actions/dropdown table]
          [bulk-actions/spreadsheet-export table]])]
      (when settings-field
        [form/component (assoc settings-field
                               :form/render header-controls
                               ;; A remount must never re-seed the sub-form —
                               ;; the live view/filter-toggle state lives there.
                               :form/suppress-setup-if-done? true)])]]))

;; ---------------------------------------------------------------------------
;; Page layout — sticky chrome around the scrolling collection
;; ---------------------------------------------------------------------------

;; The collection renders its chrome and views as flat siblings; this wrapper
;; turns that flat list into the page grid. Base: one column (table view, or
;; cards with the sidebar hidden). `.with-sidebar`: the facet sidebar takes a
;; fixed left column while the grid scrolls beside it. The pagination footer
;; pins to the viewport bottom in both.
(o/defstyled wall-layout :div
  [collection/component
   {:display "grid"
    :grid-template-columns "minmax(0, 1fr)"
    :gap "14px"}
   [cards/pagination-controls
    {:position "sticky" :bottom "0" :z-index "40"
     :background t/--hai-white-2
     :border-top-width "2px" :border-top-style "dotted"
     :border-top-color t/--note-sage-35}]]
  [:&.with-sidebar
   [collection/component
    {:grid-template-columns "240px minmax(0, 1fr)"}
    [page-header {:grid-column "1 / -1"}]
    [facets/sidebar {:grid-column "1" :grid-row "2"}]
    ;; Pin to the top of the cell: the tall sidebar makes this grid row tall, and
    ;; a stretched card grid would spread its rows to fill it, gapping the cards.
    [#{card/solution-grid cards/loading-state cards/error-state cards/no-items-state}
     {:grid-column "2" :align-self "start"}]
    [:.rt-TableRoot {:grid-column "2"}]
    [cards/pagination-controls {:grid-column "1 / -1"}]]]
  [:at-media {:max-width "768px"}
   [:&.with-sidebar
    [collection/component
     {:grid-template-columns "minmax(0, 1fr)"}
     [#{page-header facets/sidebar card/solution-grid cards/loading-state
        cards/error-state cards/no-items-state cards/pagination-controls}
      {:grid-column "1"}]
     [:.rt-TableRoot {:grid-column "1"}]
     [facets/sidebar {:grid-row "auto"}]]]])

(defn notes-table []
  [collection/component
   {:id data/table-id
    :query [{:notes/table
             [{:collection/items
               [:note/uuid
                :note/text
                :note/source
                :note/created-at
                :note/updated-at
                :note/tags
                :user.entity/permissions
                :user.entity/owner?
                sharing.components/shared-with-query
                {:note/activities [:activity/uuid :activity/url]}
                {:note/contacts [:contact/uuid :contact/full-name :contact/organisation?]}
                {:note/notes [:note/uuid :note/text]}
                {:note/tasks [:task/uuid :task/title]}]}]}]
    :paginate? true
    :pagination-position :bottom
    :pagination {:direction :desc :sort-attrs [:note/updated-at]}
    :default-view :collection.view/cards
    :controls page-chrome
    ;; Filters are set from the facet sidebar in both views; `:sibling` keeps
    ;; the table view from mounting it inside the table header.
    :filters/render facets/panel
    :filters/placement :sibling
    :no-items (fn [_] [c/Text [:em "You haven't written any notes yet."]])
    :bulk-actions [{:title "Delete"
                    :disabled? (complement sharing/owner?)
                    :mutation 'note/delete
                    :alert {:content (fn [{:table/keys [selected] :as table*} action]
                                       [bulk-actions/confirm-dialog
                                        {:table  table*
                                         :action action}
                                        [bulk-actions/title "Delete notes"]
                                        [bulk-actions/description "Are you sure you want to delete the following notes?"]
                                        (into [:ul]
                                              (map (fn [{:note/keys [text]}]
                                                     [:li [:strong (or (some-> text (subs 0 (min 50 (count text)))) "Untitled")]]))
                                              selected)])}}]
    :card note-card-render
    :columns [{:header "Note"
               :attr   :note/text
               :render (fn [{:note/keys [text title] :as note}]
                         [c/TableCell
                          ;; A long unbroken token would set the whole column's
                          ;; min-content and force the table wider than the page.
                          {:style    {:overflow-wrap "anywhere"}
                           :on-click (fn [] (>e [:routing/navigate [:notes/detail note]]))}
                          (or title
                              (subs text 0 150))])}
              {:header "Created"
               :attr   :note/created-at
               :sort?  false
               :render (fn [{:note/keys [created-at]}]
                         [c/TableCell (format-date created-at)])}
              {:header "Tags"
               :attr   :note/tags
               :sort?  false
               :render (fn [{:note/keys [tags]}]
                         [c/TableCell (str/join ", " (map :group/display-name tags))])
               ;; Value is a vector of tag names OR the `:untagged` sentinel
               ;; keyword. `:any` (not `[:or [:vector :string] :keyword]`) on
               ;; purpose, same reason as the filters below: a compound schema
               ;; trips the form's default/field instantiation. The value is
               ;; fully frontend-controlled (see the facet sidebar), so `:any`
               ;; is safe.
               :filter {:schema  :any
                        :type    :filter/select
                        :field   sidebar-only-filter-field
                        :label   "Tag"
                        :icon    icons/outlined-label
                        :summary tag-summary}}
              {:header "Contact"
               :attr   :note/contacts
               :sort?  false
               :render (fn [{:note/keys [contacts]}]
                         [c/TableCell (str/join ", " (map :contact/full-name contacts))])
               ;; Value is a single `:contact/uuid` or the `:no-contact`
               ;; sentinel keyword. `:any` on purpose — see the Tags filter
               ;; above; the value is fully frontend-controlled.
               :filter {:schema  :any
                        :type    :filter/select
                        :field   sidebar-only-filter-field
                        :label   "Contact"
                        :icon    icons/outlined-person
                        :summary contact-summary}}
              {:header "URLs"
               :attr   :note/url-domain
               :sort?  false
               :render (fn [{:note/keys [activities]}]
                         ;; URL labels are single unbreakable tokens; without
                         ;; this the longest one sets the column's min-content
                         ;; and forces a horizontal scrollbar on the table.
                         [c/TableCell {:style {:overflow-wrap "anywhere"}}
                          (str/join ", " (map (comp url/url->display-label :activity/url)
                                              activities))])
               ;; Value is a `[kind target]` tuple. `:any` (not `[:tuple :keyword :string]`)
               ;; on purpose: the form's
               ;; default-value transformer descends into the compound tuple schema and
               ;; produces a value that fails validation, so the field never validates,
               ;; gets stripped from the filters map, and every filter change then trips
               ;; "missing required key". The value is fully frontend-controlled (see the
               ;; facet sidebar), so `:any` is safe.
               :filter {:schema  :any
                        :type    :filter/select
                        :field   sidebar-only-filter-field
                        :label   "Domain"
                        :icon    icons/outlined-public
                        :summary domain-summary}}
              sharing.components/shared-with-column
              {:header ""
               :render table-actions-render}]}])

(defn page [_route]
  (let [settings @(<s [:form.field/current-value data/table-id :table/settings])
        cards?   (not= :collection.view/table (:collection/view settings))
        sidebar? (boolean (if cards?
                            (:show-filters-card? settings)
                            (:show-filters-table? settings)))]
    [layout/page
     [wall-layout {:class (when sidebar? "with-sidebar")}
      [notes-table]]]))
